/**
 * Snapshot Detail Modal
 * 
 * Displays full snapshot details including complete model list and metadata.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle, Package } from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../../utils/apiHelpers';

interface ModelMetadata {
  model_id: string;
  algorithm: string;
  model_version: string;
  accuracy?: number;
  precision?: number;
  recall?: number;
  f1_score?: number;
  deployment_status?: string;
}

interface SnapshotDetail {
  snapshot_id: string;
  tenant_id: string;
  snapshot_type: string;
  trigger_reason: string | null;
  model_count: number;
  created_at: string;
  models: ModelMetadata[];
}

interface SnapshotDetailModalProps {
  snapshotId: string;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
}

const SnapshotDetailModal: React.FC<SnapshotDetailModalProps> = ({
  snapshotId,
  tenantId,
  isOpen,
  onClose
}) => {
  const [snapshot, setSnapshot] = useState<SnapshotDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSnapshotDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = `${getApiBase()}/api/platform-admin/ml/snapshots/${tenantId}/${snapshotId}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch snapshot details: ${response.statusText}`);
      }

      const data: SnapshotDetail = await response.json();
      setSnapshot(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch snapshot details');
    } finally {
      setLoading(false);
    }
  }, [tenantId, snapshotId]);

  useEffect(() => {
    if (isOpen) {
      fetchSnapshotDetails();
    }
  }, [isOpen, snapshotId, tenantId, fetchSnapshotDetails]);

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatMetric = (value: number | undefined) => {
    if (value === undefined) return 'N/A';
    return (value * 100).toFixed(2) + '%';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Snapshot Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          ) : snapshot ? (
            <div className="space-y-6">
              {/* Snapshot Metadata */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Snapshot ID</p>
                    <p className="font-mono text-sm text-gray-900">{snapshot.snapshot_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Type</p>
                    <p className="text-sm text-gray-900 capitalize">{snapshot.snapshot_type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Created At</p>
                    <p className="text-sm text-gray-900">{formatDate(snapshot.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Model Count</p>
                    <p className="text-sm text-gray-900">{snapshot.model_count} models</p>
                  </div>
                </div>
                {snapshot.trigger_reason && (
                  <div>
                    <p className="text-sm text-gray-600">Trigger Reason</p>
                    <p className="text-sm text-gray-900">{snapshot.trigger_reason}</p>
                  </div>
                )}
              </div>

              {/* Models List */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Models in Snapshot ({snapshot.models.length})
                </h3>
                
                {snapshot.models.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <p className="text-gray-600">No models in this snapshot</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Algorithm
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Version
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Accuracy
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Precision
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Recall
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            F1 Score
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {snapshot.models.map((model, index) => (
                          <tr key={model.model_id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{model.algorithm}</div>
                              <div className="text-xs text-gray-500 font-mono">{model.model_id?.substring(0, 8)}...</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {model.model_version || 'N/A'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatMetric(model.accuracy)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatMetric(model.precision)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatMetric(model.recall)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatMetric(model.f1_score)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SnapshotDetailModal;
